import classNames from 'classnames'
import { useMemo } from 'react'

import { generateId } from 'core-components/helpers/generateId'
import { Button } from 'core-components/library/button'
import { Dropdown } from 'core-components/library/dropdown'
import {
  CONDITION_PREFIX,
  GROUP_PREFIX,
  ICondition,
  IEditor,
  IGroup,
} from 'core-components/library/filter'
import { Controller, isEmpty } from 'core-components/library/validation'
import { BaseCrud, Logic, Operator, Static } from 'core-components/types'

import { Condition } from './Condition'

import styles from './GroupWrapper.module.scss'

interface IProps {
  allowCreate?: boolean
  className?: string
  group: IGroup
  onGroupChange: (newGroupValue: IGroup) => void
  onGroupDelete?: () => void
  editors: {
    [propname: string]: IEditor
  }
  nested?: boolean
  withoutGroups?: boolean
}

export const GroupWrapper = ({
  allowCreate,
  className,
  group,
  editors,
  onGroupChange,
  onGroupDelete,
  nested,
  withoutGroups,
}: IProps) => {
  const operatorSource = useMemo(() => {
    return new BaseCrud({
      fetcher: new Static([
        { id: Logic.AND, value: Logic.AND },
        { id: Logic.OR, value: Logic.OR },
      ]),
      keyField: 'id',
    })
  }, [])

  const condition = useMemo(() => {
    const fields = Object.keys(editors)

    return {
      field: fields && fields.length === 1 ? fields[0] : '',
      operator: fields && fields.length === 1 ? editors[fields[0]].conditions[0] : ('' as Operator),
      value: '',
    }
  }, [editors])

  const addGroup = () => {
    const newGroup: IGroup = {
      id: generateId(GROUP_PREFIX),
      logic: Logic.AND,
      filters: [{ ...condition, id: generateId(CONDITION_PREFIX) }],
    }
    const newCurrentGroup = {
      ...group,
      filters: [...group.filters, newGroup],
    }
    onGroupChange(newCurrentGroup)
  }

  const addCondition = () => {
    const newCurrentGroup = {
      ...group,
      filters: [...group.filters, { ...condition, id: generateId(CONDITION_PREFIX) }],
    }
    onGroupChange(newCurrentGroup)
  }

  const deleteGroupOrCondition = (removedItem: IGroup | ICondition) => {
    const newCurrentGroup = {
      ...group,
      filters: group.filters.filter((el) => el.id !== removedItem.id),
    }
    onGroupChange(newCurrentGroup)
  }

  const handleChangeOperator = (value: Logic) => {
    const newCurrentGroup = {
      ...group,
      logic: value,
    }
    onGroupChange(newCurrentGroup)
  }

  const changeConditionOrGroup = (value: ICondition | IGroup) => {
    const newCurrentGroup = {
      ...group,
      filters: group.filters.map((el) => {
        if (el.id === value.id) {
          return value
        }
        return el
      }),
    }
    onGroupChange(newCurrentGroup)
  }

  return (
    <div className={classNames(styles.root, nested ? styles.root__nested : '', className || '')}>
      {group.filters.length > 0 ? (
        <Controller>
          <Dropdown
            classes={{ controlClasses: { root: styles.operators } }}
            source={operatorSource}
            caption={'value'}
            onChange={(value) => handleChangeOperator(value as Logic)}
            value={group.logic}
            checkFn={isEmpty}
            disabled={group.filters.length < 2}
            __testId={'logicDropdown'}
            __targetTestId={'logicDropdownTarget'}
          />
        </Controller>
      ) : null}

      {group.filters.map((el) => {
        const isGroup = 'logic' in el
        if (isGroup) {
          return (
            <GroupWrapper
              key={el.id}
              nested={true}
              group={el}
              onGroupDelete={() => {
                deleteGroupOrCondition(el)
              }}
              onGroupChange={(value) => {
                changeConditionOrGroup(value)
              }}
              editors={editors}
            />
          )
        }
        return (
          <Condition
            key={el.id}
            allowCreate={allowCreate}
            isConditionRemovable={!nested || group.filters.length > 1}
            onConditionDelete={() => {
              deleteGroupOrCondition(el)
            }}
            onConditionChange={(value) => {
              changeConditionOrGroup(value)
            }}
            condition={el}
            editors={editors}
          />
        )
      })}
      <div className={styles.actions}>
        <Button variant={'outlined'} onClick={addCondition} __testId={'AddCondition'}>
          Add condition
        </Button>
        {!withoutGroups ? (
          <Button
            className={styles.actions_btn}
            variant={'outlined'}
            onClick={addGroup}
            __testId={'AddGroup'}
          >
            Add group
          </Button>
        ) : null}
        {nested ? (
          <Button
            className={styles.actions_btn}
            variant={'outlined'}
            onClick={onGroupDelete}
            __testId={'DeleteGroup'}
          >
            Delete group
          </Button>
        ) : null}
      </div>
    </div>
  )
}
